// WESInnerReportComponent.js
import React from 'react';
import * as XLSX from 'xlsx'; // ★ 엑셀 변환을 위해 xlsx 라이브러리 임포트
import styles from './WESInnerReportComponent.module.css';
// ★ 지정해주신 새로운 로고 이미지 경로
import converLogo from '../../img/gradiantbioconvergence/gradiantbio_logo.png';

function WESInnerReportComponent({ data }) {
    if (!data) return null;

    const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    // ★ Germline 보여줄 대상인지 확인
    const isGermlineTarget = data.sampleName.startsWith('G01') || data.sampleName.startsWith('G04') || data.sampleName.startsWith('G06');

    // ★ CNV 보여줄 대상인지 확인 (샘플명의 8번째 자리가 'N'이 아닐 때만 true)
    const isCnvTarget = data.sampleName && data.sampleName.length >= 8 && data.sampleName.charAt(7).toUpperCase() !== 'N';

    // ★ Correlation(Venn Diagram) 보여줄 대상인지 확인
    const hasCorrelation = !!(data.pairedSample && data.correlationSummary);

    // ★ 파일명 파싱 유틸리티 함수 (언더바 숫자까지만 자르고 Forward/Reverse 판별)
    const getFileInfo = (rawFileName) => {
        const match = rawFileName.match(/(.*_[12])/);
        const fileName = match ? match[1] : rawFileName.split('.')[0];

        let readType = '-';
        if (fileName.endsWith('_1')) readType = 'Forward';
        else if (fileName.endsWith('_2')) readType = 'Reverse';

        return { fileName, readType };
    };

    // QC 데이터 안전 추출
    const qcDataRaw = data.qcDataRaw || [];
    const fastqcImagesRaw = data.fastqcImagesRaw || {};
    const qcDataTrim = data.qcDataTrim || [];
    const fastqcImagesTrim = data.fastqcImagesTrim || {};

    const qcDataRawPaired = data.qcDataRawPaired || [];
    const fastqcImagesRawPaired = data.fastqcImagesRawPaired || {};
    const qcDataTrimPaired = data.qcDataTrimPaired || [];
    const fastqcImagesTrimPaired = data.fastqcImagesTrimPaired || {};

    // 통합 배열 생성 (테이블 렌더링용)
    const combinedQcRaw = [...qcDataRaw, ...qcDataRawPaired];
    const combinedQcTrim = [...qcDataTrim, ...qcDataTrimPaired];

    // =================================================================
    // ★ 동적 섹션(목차) 및 페이지 번호 계산 로직
    // =================================================================
    let currentSec = 1;
    const secSummary = currentSec++;
    const secQc = currentSec++; // ★ Raw와 Trim을 하나의 섹션 번호로 통합
    const secCnv = isCnvTarget ? currentSec++ : null;
    const secMsi = currentSec++;
    const secSomatic = currentSec++;
    const secGermline = isGermlineTarget ? currentSec++ : null;
    const secPurity = currentSec++;
    const secCorrelation = hasCorrelation ? currentSec++ : null;

    let currentPage = 1;
    const pageNum_Cover = currentPage++;
    const pageNum_TOC = currentPage++;
    const pageNum_Mapping = currentPage++;

    const pageNum_OnTarget = (data.wesQcTarget || data.wesQcPaired) ? currentPage++ : null;

    const pageNum_QC_Raw_Combined = currentPage++;
    const pageNum_FastQC_Raw_Target = currentPage++;
    const pageNum_FastQC_Raw_Paired = data.pairedSample ? currentPage++ : null;

    const pageNum_QC_Trim_Combined = currentPage++;
    const pageNum_FastQC_Trim_Target = currentPage++;
    const pageNum_FastQC_Trim_Paired = data.pairedSample ? currentPage++ : null;

    const pageNum_CNV1 = isCnvTarget ? currentPage++ : null;
    const pageNum_CNV2 = isCnvTarget ? currentPage++ : null;
    const pageNum_CNV3 = isCnvTarget ? currentPage++ : null;
    const pageNum_MSI = currentPage++;

    const pageNum_Som1 = currentPage++;
    const pageNum_Som2 = currentPage++;

    const pageNum_Germ1 = isGermlineTarget ? currentPage++ : null;
    const pageNum_Germ2 = isGermlineTarget ? currentPage++ : null;
    const pageNum_Pur1 = currentPage++;
    const pageNum_Pur2 = currentPage++;
    const pageNum_Corr = hasCorrelation ? currentPage++ : null;

    const totalPages = currentPage - 1;

    // =================================================================
    // ★ 스크롤 이동 핸들러 (a href 버그 방지)
    // =================================================================
    const scrollToSec = (e, id) => {
        e.preventDefault();
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
    };

    // =================================================================
    // ★ API 다운로드 핸들러
    // =================================================================
    // 🔸 서버가 xlsx를 만들어 내려주므로 XLSX(SheetJS) import는 이 함수에서 불필요

    const handleDownloadCNV = async () => {
        try {
            const response = await fetch("/api/downloadCNV", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ files: [data.sampleName] }),
            });

            // 🔸 서버가 데이터 없음(404) 등으로 JSON을 준 경우 처리
            const contentType = response.headers.get("Content-Type") || "";
            if (!response.ok) {
                if (contentType.includes("application/json")) {
                    const errData = await response.json();
                    if (response.status === 404) {
                        alert("다운로드할 데이터가 없습니다.");
                    } else {
                        throw new Error(errData.error || "데이터를 가져오는 데 실패했습니다.");
                    }
                    return;
                }
                throw new Error("데이터를 가져오는 데 실패했습니다.");
            }

            // 🔸 정상 응답: xlsx 파일(blob)을 그대로 받아 저장
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `CNV_Result_${data.sampleName}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);

            // 🔸 누락 샘플 정보는 응답 헤더(X-Missing-Samples)에서 읽어 처리
            const missingHeader = response.headers.get("X-Missing-Samples");
            if (missingHeader) {
                let missingSamples = [];
                try {
                    missingSamples = JSON.parse(decodeURIComponent(missingHeader));
                } catch (e) {
                    missingSamples = [];
                }
                if (missingSamples.length > 0) {
                    setTimeout(() => {
                        alert(`다음 샘플은 데이터가 존재하지 않습니다:\n${missingSamples.join(", ")}`);
                    }, 500);
                }
            }
        } catch (error) {
            console.error("다운로드 실패:", error);
            alert("다운로드 중 오류가 발생했습니다.");
        }
    };

    const handleDownloadMutation = async (mutationType) => {
        try {
            const response = await fetch('/api/downloadMutation', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ files: [data.sampleName], mutationType })
            });
            if (!response.ok) throw new Error('Mutation Download failed');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const prefix = mutationType === 'germline' ? 'Germline' : 'Somatic';
            a.download = `${prefix}_Mutation_${data.sampleName}.tsv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (error) {
            console.error(error);
            alert("Mutation 데이터 다운로드에 실패했습니다.");
        }
    };

    // =================================================================
    // ★ 디자인 통일성을 위한 공통 인라인 스타일 정의
    // =================================================================
    const s_paraTitle = { fontSize: '16px', color: '#111827', fontWeight: 'bold', margin: '0 0 8px 0' };
    const s_secTitle = { fontSize: '16px', marginTop: '20px', marginBottom: '10px' };
    const s_subTitle = { fontSize: '13px', color: '#374151', fontWeight: 'bold', margin: '0 0 6px 0' };
    const s_desc = { fontSize: '12px', lineHeight: '1.6', margin: '0 0 15px 0' };
    const s_table = { fontSize: '10.5px', marginBottom: '20px' };

    const s_coverTh = { textAlign: 'left', padding: '4px 24px 4px 0', border: 'none', background: 'none', fontWeight: 'bold', color: '#374151', fontSize: '12px' };
    const s_coverTd = { textAlign: 'left', padding: '4px 0', border: 'none', background: 'none', color: '#4b5563', fontSize: '12px' };

    const descTableStyle = { ...s_table, tableLayout: 'fixed', width: '100%' };
    const descThStyle = { padding: '6px' };
    const descColTdStyle = { textAlign: 'center', padding: '6px', wordWrap: 'break-word', wordBreak: 'keep-all', whiteSpace: 'normal' };
    const descTextTdStyle = { textAlign: 'left', padding: '6px', wordWrap: 'break-word', whiteSpace: 'normal' };

    // =================================================================
    // ★ 공통 헤더(Header) 렌더링 함수
    // =================================================================
    const renderPageHeader = (title) => (
        <div className={styles.pageHeader} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ margin: 0 }}>{title}</h2>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <img
                    src={converLogo}
                    alt="Gradiant Bioconvergence Logo"
                    style={{ height: '25px', objectFit: 'contain', marginBottom: '4px' }}
                />
                <div className={styles.sampleId} style={{ margin: 0, lineHeight: '1' }}>
                    {data.sampleName}
                </div>
            </div>
        </div>
    );

    // =================================================================
    // ★ 공통 바닥글(Footer) 렌더링 함수
    // =================================================================
    const renderFooter = (pageNum) => (
        <div className={styles.pageFooter} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderTop: '1px solid #e5e7eb', paddingTop: '15px', marginTop: 'auto' }}>
            <div style={{ textAlign: 'left' }}>
                <p style={{ margin: 0, fontSize: '10px', color: '#6b7280' }}>For Research Use Only. Not intended for diagnostic procedures.</p>
                <p style={{ margin: 0, fontSize: '10px', color: '#6b7280' }}>© Gradiant Bioconvergence. All Rights Reserved.</p>
            </div>
            <div style={{ textAlign: 'right' }}>
                <p style={{ margin: '0 0 6px 0', fontSize: '11px', fontWeight: 'bold', color: '#ef4444' }}>STRICTLY CONFIDENTIAL</p>
                <p style={{ margin: 0, fontSize: '11px', fontWeight: 'bold', color: '#374151' }}>Page {pageNum} / {totalPages}</p>
            </div>
        </div>
    );

    const renderOnTargetRow = (label, key1, key2 = null) => {
        const formatValue = (qcObj) => {
            if (!qcObj) return '-';
            if (key2) {
                const v1 = qcObj[key1] || '-';
                const v2 = qcObj[key2] || '-';
                if (v1 === '-' && v2 === '-') return '-';

                const fmt1 = !isNaN(v1) && v1 !== '-' ? Number(v1).toLocaleString() : v1;
                const fmt2 = !isNaN(v2) && v2 !== '-' ? Number(v2).toLocaleString() : v2;
                return `${fmt1} (+-${fmt2})`;
            }
            const v = qcObj[key1];
            if (v === undefined || v === null || v === '-') return '-';
            if (!isNaN(v)) return Number(v).toLocaleString();
            return v;
        };

        const hasPaired = data.pairedSample && data.wesQcPaired;

        return (
            <tr>
                <td style={{ fontWeight: 'bold', textAlign: 'center', backgroundColor: 'rgb(249, 250, 251)' }}>{label}</td>
                <td style={{ textAlign: 'center' }}>{formatValue(data.wesQcTarget)}</td>
                {hasPaired && <td style={{ textAlign: 'center' }}>{formatValue(data.wesQcPaired)}</td>}
            </tr>
        );
    };

    const renderMsiTable = (sampleName, pairedSample, mappingSummary, normalMappingSummary) => {
        // 1. 텍스트 파일 데이터 파싱 함수
        const parseMsiValues = (text) => {
            let total = '-', unstable = '-', percent = '-';
            if (text) {
                const lines = text.trim().split('\n');
                if (lines.length > 1) {
                    const values = lines[1].trim().split(/\s+/);
                    if (values.length >= 3) {
                        total = values[0];
                        unstable = values[1];
                        percent = values[2]; // 파일에서 퍼센트 추출 완료
                    }
                }
            }
            return { total, unstable, percent };
        };

        // 2. 파싱 실행 (파일에서 데이터 가져오기)
        const tumorMsi = parseMsiValues(data.msiText);
        const pairedMsi = parseMsiValues(data.pairedMsiText);

        // 3. ❗DB 값이 아닌 파일에서 파싱한 퍼센트 값을 사용하도록 수정
        const tumorPercent = tumorMsi.percent;
        const pairedPercent = pairedMsi.percent;

        // 4. 기준치(10% 이상) 판별 로직
        const isTumorHigh = tumorPercent !== '-' && Number(tumorPercent) >= 10;
        const isPairedHigh = pairedPercent !== '-' && Number(pairedPercent) >= 10;

        const pairedName = pairedSample ? `${sampleName}_${pairedSample}` : null;
        const fmtNumber = (val) => (!isNaN(val) && val !== '-' ? Number(val).toLocaleString() : val);

        return (
            <table className={styles.dataTable} style={s_table}>
                <thead>
                    <tr>
                        <th style={{ width: '30%' }}>Category</th>
                        <th>Single MSI analysis</th>
                        {pairedName && <th>Paired MSI analysis</th>}
                    </tr>
                </thead>
                <tbody style={{ textAlign: 'center' }}>
                    <tr>
                        <td style={{ fontWeight: 'bold', backgroundColor: 'rgb(249, 250, 251)', textAlign: 'left' }}>Sample Name</td>
                        <td style={{ fontWeight: 'bold', color: '#111827' }}>{sampleName}</td>
                        {pairedName && <td style={{ fontWeight: 'bold', color: '#111827' }}>{pairedName}</td>}
                    </tr>
                    <tr>
                        {/* 이 부분은 이미 파일 데이터(tumorMsi.total 등)를 잘 쓰고 있었습니다! */}
                        <td style={{ fontWeight: 'bold', backgroundColor: 'rgb(249, 250, 251)', textAlign: 'left' }}>Total number of sites</td>
                        <td>{fmtNumber(tumorMsi.total)}</td>
                        {pairedName && <td>{fmtNumber(pairedMsi.total)}</td>}
                    </tr>
                    <tr>
                        <td style={{ fontWeight: 'bold', backgroundColor: 'rgb(249, 250, 251)', textAlign: 'left' }}>Number of unstable sites</td>
                        <td>{fmtNumber(tumorMsi.unstable)}</td>
                        {pairedName && <td>{fmtNumber(pairedMsi.unstable)}</td>}
                    </tr>
                    <tr>
                        {/* 파일에서 파싱한 퍼센트(tumorPercent, pairedPercent)가 출력됩니다 */}
                        <td style={{ fontWeight: 'bold', backgroundColor: 'rgb(249, 250, 251)', textAlign: 'left' }}>MSI (%)</td>
                        <td style={{ fontWeight: 'bold', color: isTumorHigh ? '#ef4444' : '#10b981' }}>
                            {tumorPercent} {isTumorHigh ? '▲' : ''}
                        </td>
                        {pairedName && (
                            <td style={{ fontWeight: 'bold', color: isPairedHigh ? '#ef4444' : '#10b981' }}>
                                {pairedPercent} {isPairedHigh ? '▲' : ''}
                            </td>
                        )}
                    </tr>
                </tbody>
            </table>
        );
    };

    const variantColumns = [
        { col: 'Hugo_Symbol', desc: 'The name of the mutated gene, following the HUGO (Human Genome Organisation) nomenclature.' },
        { col: 'Chromosome', desc: 'The chromosome number where the mutation occurred.' },
        { col: 'Start_Position', desc: 'The starting position of the mutation on the chromosome.' },
        { col: 'End_Position', desc: 'The ending position of the mutation on the chromosome.' },
        { col: 'Strand', desc: 'The direction of the DNA strand where the mutation occurred, indicated as (+) or (-).' },
        { col: 'Variant_Classification', desc: "Describes the mutation's impact on the protein (e.g., 'Missense_Mutation', 'Silent')." },
        { col: 'Variant_Type', desc: "The type of variant (e.g., 'SNP', 'DNP', 'INS', 'DEL')." },
        { col: 'Reference_Allele', desc: 'The nucleotide sequence in the reference genome.' },
        { col: 'Tumor_Allele', desc: 'The nucleotide sequence found in the tumor sample.' },
        { col: 'dbSNP_RS', desc: 'The reference SNP ID for the mutation in the dbSNP database.' },
        { col: 'HGVSc', desc: 'Description of the variant at the coding sequence (cDNA) level (HGVS notation).' },
        { col: 'HGVSp', desc: 'Description of the variant at the protein level (HGVS notation).' },
        { col: 'HGVSp_Short', desc: 'A shortened HGVS protein variant notation.' },
        { col: 't_depth', desc: 'Total sequencing depth (number of reads) at the specific position in the tumor sample.' },
        { col: 't_ref_count', desc: 'Number of reads supporting the reference allele in the tumor sample.' },
        { col: 't_alt_count', desc: 'Number of reads supporting the variant allele in the tumor sample.' },
        { col: 'n_depth', desc: 'Total sequencing depth at the specific position in the normal sample.' },
        { col: 'n_ref_count', desc: 'Number of reads supporting the reference allele in the normal sample.' },
        { col: 'n_alt_count', desc: 'Number of reads supporting the variant allele in the normal sample.' },
        { col: 'VAF', desc: 'Variant Allele Frequency, representing the proportion of the variant allele in the tumor sample.' },
        { col: 'Consequence', desc: "The functional impact of the variant on the gene (e.g., 'synonymous_variant')." },
        { col: 'Codons', desc: 'Information on the affected codons.' },
        { col: 'BIOTYPE', desc: "The biological type of the affected gene (e.g., 'protein_coding')." },
        { col: 'CANONICAL', desc: "Indicates whether the variant occurred in the canonical transcript ('YES' or 'NO')." },
        { col: 'ENSP', desc: 'The Ensembl protein ID of the affected protein.' },
        { col: 'SIFT', desc: 'SIFT score predicting the functional impact. Lower scores indicate deleterious variants.' },
        { col: 'PolyPhen', desc: 'PolyPhen score predicting the structural/functional impact. Higher scores indicate deleterious variants.' },
        { col: 'EXON / INTRON', desc: 'The exon or intron number where the variant occurred.' },
        { col: 'IMPACT', desc: "The overall impact level of the variant (e.g., 'HIGH', 'MODERATE', 'LOW', 'MODIFIER')." },
        { col: 'FILTER', desc: "Indicates the filter status. E.g., 'PASS' means it passed all quality filters." }
    ];

    const renderVennDiagram = (correlationData) => {
        if (!correlationData) return null;

        const totalTumor = Number(correlationData.tumorUniqueCount) || 0;
        const totalPair = Number(correlationData.pairUniqueCount) || 0;
        const shared = Number(correlationData.sharedCount) || 0;

        const tUniqueOnly = Math.max(0, totalTumor - shared);
        const pUniqueOnly = Math.max(0, totalPair - shared);

        if (totalTumor === 0 && totalPair === 0) return <p style={s_desc}>No correlation data available.</p>;

        const r = 80;
        const dist = 90;
        const cx1 = 200 - dist / 2;
        const cx2 = 200 + dist / 2;
        const cy = 110;
        const cyUnique = cy - 12;
        const cyShared = cy + 12;
        const sharedX = 200;
        const txtX1 = cx1 - 25;
        const txtX2 = cx2 + 25;

        const primaryName = correlationData.primaryTumor || 'Primary Tumor';
        const secondaryName = correlationData.secondaryTumor || 'Secondary Tumor';

        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '20px' }}>
                <svg width="400" height="250" viewBox="0 0 400 250" style={{ border: '1px solid #f3f4f6', backgroundColor: '#ffffff', borderRadius: '8px' }}>
                    <circle cx={cx1} cy={cy} r={r} fill="rgba(59, 130, 246, 0.4)" stroke="#3b82f6" strokeWidth="2" />
                    <circle cx={cx2} cy={cy} r={r} fill="rgba(16, 185, 129, 0.4)" stroke="#10b981" strokeWidth="2" />

                    <text x={txtX1} y={cyUnique} textAnchor="middle" alignmentBaseline="middle" fill="#111827" fontSize="15" fontWeight="bold">
                        {tUniqueOnly.toLocaleString()}
                    </text>
                    <text x={sharedX} y={cyShared} textAnchor="middle" alignmentBaseline="middle" fill="#111827" fontSize="16" fontWeight="bold">
                        {shared.toLocaleString()}
                    </text>
                    <text x={txtX2} y={cyUnique} textAnchor="middle" alignmentBaseline="middle" fill="#111827" fontSize="15" fontWeight="bold">
                        {pUniqueOnly.toLocaleString()}
                    </text>
                    <text x="110" y="225" fontSize="14" fontWeight="bold" fill="#3b82f6" textAnchor="middle">
                        {primaryName} (TO)
                    </text>
                    <text x="290" y="225" fontSize="14" fontWeight="bold" fill="#10b981" textAnchor="middle">
                        {secondaryName} (TT)
                    </text>
                </svg>
            </div>
        );
    };

    return (
        <div className={styles.reportWrapper}>

            {/* --- Page 1: 표지 --- */}
            <div className={`pdf-page ${styles.a4Page}`}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%', marginBottom: '0' }}>
                    <img src={converLogo} alt="Gradiant Bioconvergence Logo" style={{ height: '45px', objectFit: 'contain' }} />
                </div>

                <div style={{ flex: 1 }}></div>
                <div className={styles.titleSection}>
                    <div className={styles.brandLine}></div>
                    <h1>NGS WES Report</h1>
                    <p className={styles.subtitle}>Next-Generation Sequencing Data Summary</p>

                    <div className={styles.infoBoxCover}>
                        <table className={styles.coverInfoTable}>
                            <tbody>
                                <tr>
                                    <th style={s_coverTh}>Sample Name</th>
                                    <td style={s_coverTd}>{data.sampleName}</td>
                                </tr>
                                {data.pairedSample && (
                                    <tr>
                                        <th style={s_coverTh}>Paired Sample</th>
                                        <td style={s_coverTd}>{data.pairedSample}</td>
                                    </tr>
                                )}
                                <tr>
                                    <th style={s_coverTh}>Date</th>
                                    <td style={s_coverTd}>{today}</td>
                                </tr>
                                <tr>
                                    <th style={s_coverTh}>Application</th>
                                    <td style={s_coverTd}>Exome sequencing</td>
                                </tr>
                                <tr>
                                    <th style={s_coverTh}>Type of Read</th>
                                    <td style={s_coverTd}>Paired-end</td>
                                </tr>
                                <tr>
                                    <th style={s_coverTh}>Read length</th>
                                    <td style={s_coverTd}>151bp</td>
                                </tr>
                                <tr>
                                    <th style={s_coverTh}>Library Kit</th>
                                    <td style={s_coverTd}>SS6U</td>
                                </tr>
                                <tr>
                                    <th style={s_coverTh}>Type of Sequencer</th>
                                    <td style={s_coverTd}>Novaseq</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className={styles.footerSection} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div className={styles.footerNotice} style={{ margin: '0 0 6px 0' }}>For Research Use Only</div>
                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#ef4444', marginBottom: '8px' }}>STRICTLY CONFIDENTIAL</div>
                    <div style={{ fontSize: '11px', color: '#6b7280' }}>Page {pageNum_Cover} / {totalPages}</div>
                </div>
            </div>

            {/* --- Page 2: 목차 (Table of Contents) --- */}
            <div className={`pdf-page ${styles.a4Page}`}>
                {renderPageHeader('Table of Contents')}

                <div style={{ marginTop: '20px', padding: '0 20px' }}>
                    <ul style={{ listStyle: 'none', padding: 0, fontSize: '15px', lineHeight: '2.5', fontWeight: 'bold' }}>
                        <li><span onClick={(e) => scrollToSec(e, 'sec-summary')} style={{ cursor: 'pointer', color: '#2563eb' }}>{secSummary}. Analysis Summary</span></li>
                        <li><span onClick={(e) => scrollToSec(e, 'sec-qc-raw')} style={{ cursor: 'pointer', color: '#2563eb' }}>{secQc}. Quality Control</span></li>
                        {isCnvTarget && <li><span onClick={(e) => scrollToSec(e, 'sec-cnv')} style={{ cursor: 'pointer', color: '#2563eb' }}>{secCnv}. Copy Number Variation</span></li>}
                        <li><span onClick={(e) => scrollToSec(e, 'sec-msi')} style={{ cursor: 'pointer', color: '#2563eb' }}>{secMsi}. Microsatellite Instability</span></li>
                        <li><span onClick={(e) => scrollToSec(e, 'sec-somatic')} style={{ cursor: 'pointer', color: '#2563eb' }}>{secSomatic}. Somatic Variant</span></li>
                        {isGermlineTarget && <li><span onClick={(e) => scrollToSec(e, 'sec-germline')} style={{ cursor: 'pointer', color: '#2563eb' }}>{secGermline}. Germline Variant Call</span></li>}
                        <li><span onClick={(e) => scrollToSec(e, 'sec-purity')} style={{ cursor: 'pointer', color: '#2563eb' }}>{secPurity}. Tumor Purity</span></li>
                        {hasCorrelation && <li><span onClick={(e) => scrollToSec(e, 'sec-correlation')} style={{ cursor: 'pointer', color: '#2563eb' }}>{secCorrelation}. Variant Correlation</span></li>}
                    </ul>
                </div>

                {renderFooter(pageNum_TOC)}
            </div>

            {/* --- Page 3: Mapping Summary --- */}
            <div className={`pdf-page ${styles.a4Page}`} id="sec-summary">
                {renderPageHeader(`${secSummary}. Analysis Summary`)}

                <p className={styles.description} style={{ ...s_desc, marginBottom: '20px' }}>
                    An overview of the overall data quality and the key mapping results of this NGS data analysis. The table displays the Total Read, representing the total volume of generated sequencing data, along with the Mapped Read, indicating the number of reads successfully aligned to the reference genome, and its corresponding percentage (Mapping Rate, %).
                </p>

                <h3 className={styles.sectionTitle} style={{ ...s_secTitle, marginTop: '0', marginBottom: '8px' }}>Mapping Summary</h3>
                <table className={styles.dataTable} style={s_table}>
                    <thead>
                        <tr>
                            <th>Sample Name</th>
                            <th>Total Read</th>
                            <th>Mapped Read</th>
                            <th>Mapping Rate (%)</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>{data.mappingSummary?.sampleName || data.sampleName}</td>
                            <td>{data.mappingSummary?.totalRead ? Number(data.mappingSummary.totalRead).toLocaleString() : '-'}</td>
                            <td>{data.mappingSummary?.mappedRead ? Number(data.mappingSummary.mappedRead).toLocaleString() : '-'}</td>
                            <td>{data.mappingSummary?.percent || '-'}</td>
                        </tr>
                        {data.pairedSample && data.normalMappingSummary && (
                            <tr>
                                <td>{data.normalMappingSummary.sampleName || data.pairedSample}</td>
                                <td>{data.normalMappingSummary.totalRead ? Number(data.normalMappingSummary.totalRead).toLocaleString() : '-'}</td>
                                <td>{data.normalMappingSummary.mappedRead ? Number(data.normalMappingSummary.mappedRead).toLocaleString() : '-'}</td>
                                <td>{data.normalMappingSummary.percent || '-'}</td>
                            </tr>
                        )}
                    </tbody>
                </table>

                <h3 className={styles.sectionTitle} style={s_secTitle}>Column Description</h3>
                <table className={styles.dataTable} style={descTableStyle}>
                    <thead>
                        <tr>
                            <th style={{ width: '20%', ...descThStyle }}>Column</th>
                            <th style={descThStyle}>Description</th>
                        </tr>
                    </thead>
                    <tbody style={{ lineHeight: '1.4' }}>
                        <tr><td style={descColTdStyle}><strong>Total Read</strong></td><td style={descTextTdStyle}>The total number of reads generated from the sequencing process.</td></tr>
                        <tr><td style={descColTdStyle}><strong>Mapped Read</strong></td><td style={descTextTdStyle}>The number of reads that successfully aligned to the reference genome.</td></tr>
                        <tr><td style={descColTdStyle}><strong>Mapping Rate (%)</strong></td><td style={descTextTdStyle}>The ratio of mapped reads to total reads, indicating alignment efficiency.</td></tr>
                    </tbody>
                </table>

                {renderFooter(pageNum_Mapping)}
            </div>

            {/* --- Page 4: On-target coverage --- */}
            {(data.wesQcTarget || data.wesQcPaired) && (
                <div className={`pdf-page ${styles.a4Page}`}>
                    {renderPageHeader(`${secSummary}. Analysis Summary`)}

                    <h3 className={styles.sectionTitle} style={{ ...s_secTitle, marginTop: '0', marginBottom: '8px' }}>On-target Coverage</h3>
                    <table className={styles.dataTable} style={s_table}>
                        <thead>
                            <tr>
                                <th style={{ width: '35%' }}>Metric</th>
                                <th>{data.sampleName}</th>
                                {data.pairedSample && data.wesQcPaired && <th>{data.pairedSample}</th>}
                            </tr>
                        </thead>
                        <tbody style={{ textAlign: 'left' }}>
                            {renderOnTargetRow('Total base', 'total_base_bp')}
                            {renderOnTargetRow('On-target base', 'target_region_bp')}
                            {renderOnTargetRow('On-target bases >=1X (%)', 'on_target_base_1x_pct')}
                            {renderOnTargetRow('On-target bases >=10X (%)', 'on_target_base_10x_pct')}
                            {renderOnTargetRow('On-target bases >=20X (%)', 'on_target_base_20x_pct')}
                            {renderOnTargetRow('On-target bases >=30X (%)', 'on_target_base_30x_pct')}
                            {renderOnTargetRow('On-target bases >=50X (%)', 'on_target_base_50x_pct')}
                            {renderOnTargetRow('Average insert size (bp)', 'average_insert_size_bp', 'insert_size_sd_bp')}
                        </tbody>
                    </table>

                    <h3 className={styles.sectionTitle} style={s_secTitle}>Column Description</h3>
                    <table className={styles.dataTable} style={descTableStyle}>
                        <thead>
                            <tr>
                                <th style={{ width: '25%', ...descThStyle }}>Column</th>
                                <th style={descThStyle}>Description</th>
                            </tr>
                        </thead>
                        <tbody style={{ lineHeight: '1.4' }}>
                            <tr><td style={descColTdStyle}><strong>Total base</strong></td><td style={descTextTdStyle}>The total number of bases in the adapter trimmed reads</td></tr>
                            <tr><td style={descColTdStyle}><strong>On-target base</strong></td><td style={descTextTdStyle}>The total number of bases mapped to target regions</td></tr>
                            <tr><td style={descColTdStyle}><strong>On-target bases &gt;=1X (%)</strong></td><td style={descTextTdStyle}>The ratio of target bases with depth &gt;= 1x</td></tr>
                            <tr><td style={descColTdStyle}><strong>On-target bases &gt;=10X (%)</strong></td><td style={descTextTdStyle}>The ratio of target bases with depth &gt;= 10x</td></tr>
                            <tr><td style={descColTdStyle}><strong>On-target bases &gt;=20X (%)</strong></td><td style={descTextTdStyle}>The ratio of target bases with depth &gt;= 20x</td></tr>
                            <tr><td style={descColTdStyle}><strong>On-target bases &gt;=30X (%)</strong></td><td style={descTextTdStyle}>The ratio of target bases with depth &gt;= 30x</td></tr>
                            <tr><td style={descColTdStyle}><strong>On-target bases &gt;=50X (%)</strong></td><td style={descTextTdStyle}>The ratio of target bases with depth &gt;= 50x</td></tr>
                            <tr><td style={descColTdStyle}><strong>Average insert size (bp)</strong></td><td style={descTextTdStyle}>Insert size is shown as mean (+-SD), calculated from the median absolute deviation (MAD)-trimmed distribution of proper read pairs</td></tr>
                        </tbody>
                    </table>

                    {renderFooter(pageNum_OnTarget)}
                </div>
            )}

            {/* --- Page 5: Quality Control (Raw file) - Combined Table --- */}
            <div className={`pdf-page ${styles.a4Page}`} id="sec-qc-raw">
                {renderPageHeader(`${secQc}. Quality Control (Raw file)`)}

                <p className={styles.description} style={{ ...s_desc, marginBottom: '15px' }}>
                    Quality control of raw sequencing data was performed using FastQC (v0.12.1). Adapters and low-quality bases were removed using Trimmomatic (v0.39), and sequence data manipulation and summary statistics were processed with SeqKit (v2.13.0).
                </p>

                <h3 className={styles.sectionTitle} style={{ ...s_secTitle, marginTop: '10px', marginBottom: '8px' }}>QC Summary</h3>
                <table className={styles.dataTable} style={s_table}>
                    <thead>
                        <tr>
                            <th>File Name</th>
                            <th>Type</th>
                            <th>Total reads</th>
                            <th>Total base pair</th>
                            <th>Q20(%)</th>
                            <th>Q30(%)</th>
                            <th>GC(%)</th>
                        </tr>
                    </thead>
                    <tbody style={{ textAlign: 'center' }}>
                        {combinedQcRaw.length > 0 ? (
                            combinedQcRaw.map((qc, idx) => {
                                const { fileName, readType } = getFileInfo(qc.type);
                                return (
                                    <tr key={idx}>
                                        <td style={{ fontWeight: 'bold' }}>{fileName}</td>
                                        <td>{readType}</td>
                                        <td>{qc.totalReads ? Number(qc.totalReads).toLocaleString() : '-'}</td>
                                        <td>{qc.totalBasePair ? Number(qc.totalBasePair).toLocaleString() : '-'}</td>
                                        <td>{qc.q20 || '-'}</td>
                                        <td>{qc.q30 || '-'}</td>
                                        <td>{qc.gc || '-'}</td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr><td colSpan="7" style={{ padding: '20px', color: '#9ca3af' }}>No QC data available.</td></tr>
                        )}
                    </tbody>
                </table>

                <h3 className={styles.sectionTitle} style={s_secTitle}>Column Description</h3>
                <table className={styles.dataTable} style={descTableStyle}>
                    <thead>
                        <tr>
                            <th style={{ width: '25%', ...descThStyle }}>Column</th>
                            <th style={descThStyle}>Description</th>
                        </tr>
                    </thead>
                    <tbody style={{ lineHeight: '1.4' }}>
                        <tr><td style={descColTdStyle}><strong>File Name</strong></td><td style={descTextTdStyle}>Name of the sequencing file</td></tr>
                        <tr><td style={descColTdStyle}><strong>Type</strong></td><td style={descTextTdStyle}>Information of paired reads (Forward or Reverse)</td></tr>
                        <tr><td style={descColTdStyle}><strong>Total reads</strong></td><td style={descTextTdStyle}>Number of sequences</td></tr>
                        <tr><td style={descColTdStyle}><strong>Total base pair</strong></td><td style={descTextTdStyle}>Total number of bases</td></tr>
                        <tr><td style={descColTdStyle}><strong>Q20(%)</strong></td><td style={descTextTdStyle}>Percentage of bases with quality scores over 20</td></tr>
                        <tr><td style={descColTdStyle}><strong>Q30(%)</strong></td><td style={descTextTdStyle}>Percentage of bases with quality scores over 30</td></tr>
                        <tr><td style={descColTdStyle}><strong>GC(%)</strong></td><td style={descTextTdStyle}>Percentage of GC content</td></tr>
                    </tbody>
                </table>

                {renderFooter(pageNum_QC_Raw_Combined)}
            </div>

            {/* --- Page 6: Quality Control (Raw file) - Target Images --- */}
            <div className={`pdf-page ${styles.a4Page}`}>
                {renderPageHeader(`${secQc}. Quality Control (Raw file)`)}

                <h3 className={styles.sectionTitle} style={{ ...s_secTitle, marginTop: '0', marginBottom: '8px' }}>Per Base Sequence Quality</h3>
                <p className={styles.description} style={s_desc}>
                    The plots below show the quality scores across all bases in the sequencing reads. A higher score indicates better quality. The yellow box represents the inter-quartile range, and the red line represents the median quality.
                </p>

                {/* Target Read 1 */}
                <h4 style={s_subTitle}>Read 1 ({data.sampleName})</h4>
                <div style={{ width: '100%', height: '350px', backgroundColor: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: '10px', boxSizing: 'border-box', marginBottom: '20px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                    {fastqcImagesRaw.read1 ? (
                        <img src={fastqcImagesRaw.read1} alt="Target Read 1 FastQC" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    ) : (
                        <span style={{ color: '#9ca3af', fontWeight: 'bold' }}>[Target Read 1 FastQC Image Not Found]</span>
                    )}
                </div>

                {/* Target Read 2 */}
                <h4 style={s_subTitle}>Read 2 ({data.sampleName})</h4>
                <div style={{ width: '100%', height: '350px', backgroundColor: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: '10px', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                    {fastqcImagesRaw.read2 ? (
                        <img src={fastqcImagesRaw.read2} alt="Target Read 2 FastQC" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    ) : (
                        <span style={{ color: '#9ca3af', fontWeight: 'bold' }}>[Target Read 2 FastQC Image Not Found]</span>
                    )}
                </div>

                {renderFooter(pageNum_FastQC_Raw_Target)}
            </div>

            {/* --- Page 7: Quality Control (Raw file) - Paired Images (조건부) --- */}
            {data.pairedSample && (
                <div className={`pdf-page ${styles.a4Page}`}>
                    {renderPageHeader(`${secQc}. Quality Control (Raw file)`)}

                    <h3 className={styles.sectionTitle} style={{ ...s_secTitle, marginTop: '0', marginBottom: '8px' }}>Per Base Sequence Quality (Paired)</h3>
                    <p className={styles.description} style={s_desc}>
                        The plots below show the quality scores across all bases in the sequencing reads. A higher score indicates better quality. The yellow box represents the inter-quartile range, and the red line represents the median quality.
                    </p>

                    {/* Paired Read 1 */}
                    <h4 style={s_subTitle}>Read 1 ({data.pairedSample})</h4>
                    <div style={{ width: '100%', height: '350px', backgroundColor: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: '10px', boxSizing: 'border-box', marginBottom: '20px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                        {fastqcImagesRawPaired.read1 ? (
                            <img src={fastqcImagesRawPaired.read1} alt="Paired Read 1 FastQC" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        ) : (
                            <span style={{ color: '#9ca3af', fontWeight: 'bold' }}>[Paired Read 1 FastQC Image Not Found]</span>
                        )}
                    </div>

                    {/* Paired Read 2 */}
                    <h4 style={s_subTitle}>Read 2 ({data.pairedSample})</h4>
                    <div style={{ width: '100%', height: '350px', backgroundColor: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: '10px', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                        {fastqcImagesRawPaired.read2 ? (
                            <img src={fastqcImagesRawPaired.read2} alt="Paired Read 2 FastQC" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        ) : (
                            <span style={{ color: '#9ca3af', fontWeight: 'bold' }}>[Paired Read 2 FastQC Image Not Found]</span>
                        )}
                    </div>

                    {renderFooter(pageNum_FastQC_Raw_Paired)}
                </div>
            )}

            {/* --- Page 8: Quality Control (Trimmed file) - Combined Table --- */}
            <div className={`pdf-page ${styles.a4Page}`} id="sec-qc-trim">
                {renderPageHeader(`${secQc}. Quality Control (Trimmed file)`)}

                <p className={styles.description} style={{ ...s_desc, marginBottom: '15px' }}>
                    Quality control of trimmed sequencing data was performed using FastQC (v0.12.1). Adapters and low-quality bases were removed using Trimmomatic (v0.39), and sequence data manipulation and summary statistics were processed with SeqKit (v2.13.0).
                </p>

                <h3 className={styles.sectionTitle} style={{ ...s_secTitle, marginTop: '10px', marginBottom: '8px' }}>QC Summary</h3>
                <table className={styles.dataTable} style={s_table}>
                    <thead>
                        <tr>
                            <th>File Name</th>
                            <th>Type</th>
                            <th>Total reads</th>
                            <th>Total base pair</th>
                            <th>Q20(%)</th>
                            <th>Q30(%)</th>
                            <th>GC(%)</th>
                        </tr>
                    </thead>
                    <tbody style={{ textAlign: 'center' }}>
                        {combinedQcTrim.length > 0 ? (
                            combinedQcTrim.map((qc, idx) => {
                                const { fileName, readType } = getFileInfo(qc.type);
                                return (
                                    <tr key={idx}>
                                        <td style={{ fontWeight: 'bold' }}>{fileName}</td>
                                        <td>{readType}</td>
                                        <td>{qc.totalReads ? Number(qc.totalReads).toLocaleString() : '-'}</td>
                                        <td>{qc.totalBasePair ? Number(qc.totalBasePair).toLocaleString() : '-'}</td>
                                        <td>{qc.q20 || '-'}</td>
                                        <td>{qc.q30 || '-'}</td>
                                        <td>{qc.gc || '-'}</td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr><td colSpan="7" style={{ padding: '20px', color: '#9ca3af' }}>No QC data available.</td></tr>
                        )}
                    </tbody>
                </table>

                <h3 className={styles.sectionTitle} style={s_secTitle}>Column Description</h3>
                <table className={styles.dataTable} style={descTableStyle}>
                    <thead>
                        <tr>
                            <th style={{ width: '25%', ...descThStyle }}>Column</th>
                            <th style={descThStyle}>Description</th>
                        </tr>
                    </thead>
                    <tbody style={{ textAlign: 'left', lineHeight: '1.4' }}>
                        <tr><td style={descColTdStyle}><strong>File Name</strong></td><td style={descTextTdStyle}>Name of the sequencing file</td></tr>
                        <tr><td style={descColTdStyle}><strong>Type</strong></td><td style={descTextTdStyle}>Information of paired reads (Forward or Reverse)</td></tr>
                        <tr><td style={descColTdStyle}><strong>Total reads</strong></td><td style={descTextTdStyle}>Number of sequences</td></tr>
                        <tr><td style={descColTdStyle}><strong>Total base pair</strong></td><td style={descTextTdStyle}>Total number of bases</td></tr>
                        <tr><td style={descColTdStyle}><strong>Q20(%)</strong></td><td style={descTextTdStyle}>Percentage of bases with quality scores over 20</td></tr>
                        <tr><td style={descColTdStyle}><strong>Q30(%)</strong></td><td style={descTextTdStyle}>Percentage of bases with quality scores over 30</td></tr>
                        <tr><td style={descColTdStyle}><strong>GC(%)</strong></td><td style={descTextTdStyle}>Percentage of GC content</td></tr>
                    </tbody>
                </table>

                {renderFooter(pageNum_QC_Trim_Combined)}
            </div>

            {/* --- Page 9: Quality Control (Trimmed file) - Target Images --- */}
            <div className={`pdf-page ${styles.a4Page}`}>
                {renderPageHeader(`${secQc}. Quality Control (Trimmed file)`)}

                <h3 className={styles.sectionTitle} style={{ ...s_secTitle, marginTop: '0', marginBottom: '8px' }}>Per Base Sequence Quality</h3>
                <p className={styles.description} style={s_desc}>
                    The plots below show the quality scores across all bases in the sequencing reads. A higher score indicates better quality. The yellow box represents the inter-quartile range, and the red line represents the median quality.
                </p>

                {/* Target Read 1 Trim */}
                <h4 style={s_subTitle}>Read 1 ({data.sampleName})</h4>
                <div style={{ width: '100%', height: '350px', backgroundColor: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: '10px', boxSizing: 'border-box', marginBottom: '20px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                    {fastqcImagesTrim.read1 ? (
                        <img src={fastqcImagesTrim.read1} alt="Target Read 1 Trim FastQC" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    ) : (
                        <span style={{ color: '#9ca3af', fontWeight: 'bold' }}>[Target Read 1 Trim FastQC Image Not Found]</span>
                    )}
                </div>

                {/* Target Read 2 Trim */}
                <h4 style={s_subTitle}>Read 2 ({data.sampleName})</h4>
                <div style={{ width: '100%', height: '350px', backgroundColor: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: '10px', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                    {fastqcImagesTrim.read2 ? (
                        <img src={fastqcImagesTrim.read2} alt="Target Read 2 Trim FastQC" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    ) : (
                        <span style={{ color: '#9ca3af', fontWeight: 'bold' }}>[Target Read 2 Trim FastQC Image Not Found]</span>
                    )}
                </div>

                {renderFooter(pageNum_FastQC_Trim_Target)}
            </div>

            {/* --- Page 10: Quality Control (Trimmed file) - Paired Images (조건부) --- */}
            {data.pairedSample && (
                <div className={`pdf-page ${styles.a4Page}`}>
                    {renderPageHeader(`${secQc}. Quality Control (Trimmed file)`)}

                    <h3 className={styles.sectionTitle} style={{ ...s_secTitle, marginTop: '0', marginBottom: '8px' }}>Per Base Sequence Quality (Paired)</h3>
                    <p className={styles.description} style={s_desc}>
                        The plots below show the quality scores across all bases in the sequencing reads. A higher score indicates better quality. The yellow box represents the inter-quartile range, and the red line represents the median quality.
                    </p>

                    {/* Paired Read 1 Trim */}
                    <h4 style={s_subTitle}>Read 1 ({data.pairedSample})</h4>
                    <div style={{ width: '100%', height: '350px', backgroundColor: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: '10px', boxSizing: 'border-box', marginBottom: '20px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                        {fastqcImagesTrimPaired.read1 ? (
                            <img src={fastqcImagesTrimPaired.read1} alt="Paired Read 1 Trim FastQC" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        ) : (
                            <span style={{ color: '#9ca3af', fontWeight: 'bold' }}>[Paired Read 1 Trim FastQC Image Not Found]</span>
                        )}
                    </div>

                    {/* Paired Read 2 Trim */}
                    <h4 style={s_subTitle}>Read 2 ({data.pairedSample})</h4>
                    <div style={{ width: '100%', height: '350px', backgroundColor: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: '10px', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                        {fastqcImagesTrimPaired.read2 ? (
                            <img src={fastqcImagesTrimPaired.read2} alt="Paired Read 2 Trim FastQC" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        ) : (
                            <span style={{ color: '#9ca3af', fontWeight: 'bold' }}>[Paired Read 2 Trim FastQC Image Not Found]</span>
                        )}
                    </div>

                    {renderFooter(pageNum_FastQC_Trim_Paired)}
                </div>
            )}

            {/* 조건부 렌더링: isCnvTarget이 true일 때만 CNV 페이지 출력 */}
            {isCnvTarget && (
                <>
                    {/* --- Page 11: CNV Introduction & Table --- */}
                    <div className={`pdf-page ${styles.a4Page}`} id="sec-cnv">
                        {renderPageHeader(`${secCnv}. Copy Number Variation`)}

                        <h3 style={s_paraTitle}>CNV (Copy Number Variation) Analysis</h3>
                        <p className={styles.description} style={s_desc}>
                            Amplification or deletion status can be identified at the chromosome (segment) or gene level. CNVkit python library (v0.9.10) was used for the analysis.
                        </p>

                        <h3 className={styles.sectionTitle} style={s_secTitle}>CNV Summary</h3>
                        <table className={styles.dataTable} style={s_table}>
                            <thead>
                                <tr>
                                    <th>Level</th>
                                    <th>Amplification</th>
                                    <th>Deletion</th>
                                    <th>Diploid</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={{ fontWeight: 'bold' }}>Segment Level</td>
                                    <td>{data.cnvTypeCounts?.segment?.Amplification ? Number(data.cnvTypeCounts.segment.Amplification).toLocaleString() : 0}</td>
                                    <td>{data.cnvTypeCounts?.segment?.Deletion ? Number(data.cnvTypeCounts.segment.Deletion).toLocaleString() : 0}</td>
                                    <td>{data.cnvTypeCounts?.segment?.Diploid ? Number(data.cnvTypeCounts.segment.Diploid).toLocaleString() : 0}</td>
                                </tr>
                                <tr>
                                    <td style={{ fontWeight: 'bold' }}>Gene Level</td>
                                    <td>{data.cnvTypeCounts?.gene?.Amplification ? Number(data.cnvTypeCounts.gene.Amplification).toLocaleString() : 0}</td>
                                    <td>{data.cnvTypeCounts?.gene?.Deletion ? Number(data.cnvTypeCounts.gene.Deletion).toLocaleString() : 0}</td>
                                    <td>{data.cnvTypeCounts?.gene?.Diploid ? Number(data.cnvTypeCounts.gene.Diploid).toLocaleString() : 0}</td>
                                </tr>
                            </tbody>
                        </table>

                        <h3 className={styles.sectionTitle} style={s_secTitle}>
                            CNV Result Table
                            <button
                                onClick={handleDownloadCNV}
                                style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', marginLeft: '10px' }}
                            >
                                (<span style={{ textDecoration: 'underline' }}>Download</span>)
                            </button>
                        </h3>

                        <h4 style={s_subTitle}>1-1 SegmentLevel sheet</h4>
                        <table className={styles.dataTable} style={{ ...descTableStyle, fontSize: '9.5px', marginBottom: '15px' }}>
                            <thead>
                                <tr><th style={{ width: '20%', ...descThStyle }}>Column</th><th style={descThStyle}>Description</th></tr>
                            </thead>
                            <tbody style={{ lineHeight: '1.3' }}>
                                <tr><td style={descColTdStyle}><strong>NGS Code</strong></td><td style={descTextTdStyle}>Unique 16-digit identifier for NGS data.</td></tr>
                                <tr><td style={descColTdStyle}><strong>chromosome</strong></td><td style={descTextTdStyle}>Chromosome number or name (e.g., 1, 2, X) where the segment is located.</td></tr>
                                <tr><td style={descColTdStyle}><strong>start</strong></td><td style={descTextTdStyle}>Starting position of the segment on the chromosome (in bp).</td></tr>
                                <tr><td style={descColTdStyle}><strong>end</strong></td><td style={descTextTdStyle}>Ending position of the segment on the chromosome (in bp).</td></tr>
                                <tr><td style={descColTdStyle}><strong>Log2 Copy Number</strong></td><td style={descTextTdStyle}>Log2 mean value of coverage depth in the segment, excluding extreme outliers.</td></tr>
                                <tr><td style={descColTdStyle}><strong>Absolute Copy Number</strong></td><td style={descTextTdStyle}>Estimated absolute copy number from log2 value (rounded to integer).</td></tr>
                                <tr><td style={descColTdStyle}><strong>depth</strong></td><td style={descTextTdStyle}>Robust mean of coverage depth calculated on an absolute scale without bias correction.</td></tr>
                                <tr><td style={descColTdStyle}><strong>probes</strong></td><td style={descTextTdStyle}>Number of bins (analysis units) included in the segment.</td></tr>
                                <tr><td style={descColTdStyle}><strong>weight</strong></td><td style={descTextTdStyle}>Segment-specific weight, calculated as the sum of bin-level weights.</td></tr>
                                <tr><td style={descColTdStyle}><strong>p-ttest</strong></td><td style={descTextTdStyle}>p-value from t-test compared to the reference.</td></tr>
                                <tr><td style={descColTdStyle}><strong>CNV type</strong></td><td style={descTextTdStyle}>Classification based on Absolute Copy Number (Amplification/Diploid/Deletion).</td></tr>
                                <tr><td style={descColTdStyle}><strong>Gene list</strong></td><td style={descTextTdStyle}>List of gene names included in the segment.</td></tr>
                            </tbody>
                        </table>

                        <h4 style={s_subTitle}>1-2 GeneLevel sheet</h4>
                        <p className={styles.description} style={{ marginTop: '0', marginBottom: '0', fontSize: '11px' }}>
                            The 'geneLevel' sheet displays the CNV status for the genes in the first column of each sample. Insignificant results are left blank.
                        </p>

                        {renderFooter(pageNum_CNV1)}
                    </div>

                    {/* --- Page 12: Diagram --- */}
                    <div className={`pdf-page ${styles.a4Page}`}>
                        {renderPageHeader(`${secCnv}. Copy Number Variation`)}

                        <h3 style={s_paraTitle}>Diagram</h3>
                        <p className={styles.description} style={s_desc}>
                            This is a summary figure overlaying copy number information on a chromosome ideogram. For each chromosome, it visualizes the segment-level pattern on the left and the bin-level pattern on the right. The segment panel shows the mean log2 value for each region, while the bin panel displays detailed patterns. It provides an intuitive overview of which regions are generally amplified or deleted across each chromosome.
                        </p>

                        <div className={styles.imageContainer}>
                            {data.diagramImg ? (
                                <img src={data.diagramImg} alt="CNV Diagram" />
                            ) : (
                                <span className={styles.errorText}>[Diagram image not available]</span>
                            )}
                        </div>

                        {renderFooter(pageNum_CNV2)}
                    </div>

                    {/* --- Page 13: Scatter Plot --- */}
                    <div className={`pdf-page ${styles.a4Page}`}>
                        {renderPageHeader(`${secCnv}. Copy Number Variation`)}

                        <h3 style={s_paraTitle}>Scatter Plot</h3>
                        <p className={styles.description} style={s_desc}>
                            A scatter plot showing the bin-level log2(copy-ratio) and segments of a single sample. The entire genome is arranged in chromosomal order, with normalized log2 copy ratio values of each bin plotted as dots. The mean copy number for each segment is shown as a line. The x-axis represents the genomic position, and the y-axis represents the log2(copy ratio). A value of 0 indicates diploid, {`>0`} indicates amplification, and {`<0`} indicates deletion.
                        </p>

                        <div className={styles.imageContainer}>
                            {data.scatterImg ? (
                                <img src={data.scatterImg} alt="CNV Scatter Plot" />
                            ) : (
                                <span className={styles.errorText}>[Scatter Plot image not available]</span>
                            )}
                        </div>

                        {renderFooter(pageNum_CNV3)}
                    </div>
                </>
            )}

            {/* --- Page 14: Microsatellite Instability --- */}
            <div className={`pdf-page ${styles.a4Page}`} id="sec-msi">
                {renderPageHeader(`${secMsi}. Microsatellite Instability`)}

                <h3 style={s_paraTitle}>MSI (Microsatellite Instability) Analysis</h3>
                <p className={styles.description} style={s_desc}>
                    MSI (Microsatellite Instability) analysis evaluates DNA replication errors and mismatch repair (MMR) dysfunction by detecting length variations in microsatellite repeat regions. The status is classified as MSI-H (High), MSI-L (Low), or MSS (Stable) based on the proportion of unstable sites. Samples with an MSI ratio of 10% or higher are categorized as MSI-H and are indicated by a red triangle (▲) icon.
                </p>

                <h3 className={styles.sectionTitle} style={s_secTitle}>Result Table</h3>
                {renderMsiTable(data.sampleName, data.pairedSample, data.mappingSummary, data.normalMappingSummary)}

                {renderFooter(pageNum_MSI)}
            </div>

            {/* --- Page 15: Somatic Variant Call (Mutation Summary) --- */}
            <div className={`pdf-page ${styles.a4Page}`} id="sec-somatic">
                {renderPageHeader(`${secSomatic}. Somatic Variant`)}

                <p className={styles.description} style={{ ...s_desc, marginBottom: '20px' }}>
                    This section briefly provides the analysis results of somatic variants identified in the target sample using our GATK-based in-house pipeline. By filtering out germline variants and false positive errors that can occur during the sequencing process, you can find the Variant Allele Frequency (VAF) and related gene information for significant variants such as Single Nucleotide Variants (SNV) and Insertions/Deletions (Indel).
                </p>

                <h3 className={styles.sectionTitle} style={{ ...s_secTitle, marginTop: '0', marginBottom: '8px' }}>Mutation Summary</h3>

                <div style={{ width: '60%', margin: '0 auto 20px auto', border: '1px solid #d1d5db', fontSize: '10.5px', color: '#4b5563' }}>
                    <div style={{ display: 'flex', backgroundColor: 'rgb(243, 244, 246)', fontWeight: 'bold', color: '#374151', borderBottom: '1px solid #d1d5db', textAlign: 'center' }}>
                        <div style={{ width: '75%', padding: '6px 12px', borderRight: '1px solid #d1d5db' }}>Category</div>
                        <div style={{ width: '25%', padding: '6px 12px' }}>Count</div>
                    </div>

                    <div style={{ display: 'flex', borderBottom: '1px solid #d1d5db' }}>
                        <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', borderBottom: '1px solid #d1d5db', backgroundColor: 'rgb(250, 250, 250)' }}>
                                <div style={{ width: '75%', padding: '6px 12px', borderRight: '1px solid #d1d5db', textAlign: 'left' }}>SNP</div>
                                <div style={{ width: '25%', padding: '6px 12px', textAlign: 'center' }}>{data.mutationCounts?.SNP ? Number(data.mutationCounts.SNP).toLocaleString() : 0}</div>
                            </div>
                            <div style={{ display: 'flex', backgroundColor: 'rgb(250, 250, 250)' }}>
                                <div style={{ width: '75%', padding: '6px 12px', borderRight: '1px solid #d1d5db', textAlign: 'left' }}>INDEL (INS/DEL)</div>
                                <div style={{ width: '25%', padding: '6px 12px', textAlign: 'center' }}>{data.mutationCounts?.INDEL ? Number(data.mutationCounts.INDEL).toLocaleString() : 0}</div>
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'flex' }}>
                        <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', borderBottom: '1px solid #d1d5db', backgroundColor: 'rgb(250, 250, 250)' }}>
                                <div style={{ width: '75%', padding: '6px 12px', borderRight: '1px solid #d1d5db', textAlign: 'left' }}>Synonymous Variant</div>
                                <div style={{ width: '25%', padding: '6px 12px', textAlign: 'center' }}>{data.mutationCounts?.Synonymous ? Number(data.mutationCounts.Synonymous).toLocaleString() : 0}</div>
                            </div>
                            <div style={{ display: 'flex', borderBottom: '1px solid #d1d5db', backgroundColor: 'rgb(250, 250, 250)' }}>
                                <div style={{ width: '75%', padding: '6px 12px', borderRight: '1px solid #d1d5db', textAlign: 'left' }}>Missense Variant</div>
                                <div style={{ width: '25%', padding: '6px 12px', textAlign: 'center' }}>{data.mutationCounts?.Missense ? Number(data.mutationCounts.Missense).toLocaleString() : 0}</div>
                            </div>
                            <div style={{ display: 'flex', borderBottom: '1px solid #d1d5db', backgroundColor: 'rgb(250, 250, 250)' }}>
                                <div style={{ width: '75%', padding: '6px 12px', borderRight: '1px solid #d1d5db', textAlign: 'left' }}>Stop Gain</div>
                                <div style={{ width: '25%', padding: '6px 12px', textAlign: 'center' }}>{data.mutationCounts?.StopGain ? Number(data.mutationCounts.StopGain).toLocaleString() : 0}</div>
                            </div>
                            <div style={{ display: 'flex', borderBottom: '1px solid #d1d5db', backgroundColor: 'rgb(250, 250, 250)' }}>
                                <div style={{ width: '75%', padding: '6px 12px', borderRight: '1px solid #d1d5db', textAlign: 'left' }}>Stop Lost</div>
                                <div style={{ width: '25%', padding: '6px 12px', textAlign: 'center' }}>{data.mutationCounts?.StopLost ? Number(data.mutationCounts.StopLost).toLocaleString() : 0}</div>
                            </div>
                            <div style={{ display: 'flex', borderBottom: '1px solid #d1d5db', backgroundColor: 'rgb(250, 250, 250)' }}>
                                <div style={{ width: '75%', padding: '6px 12px', borderRight: '1px solid #d1d5db', textAlign: 'left' }}>Frameshift Variant</div>
                                <div style={{ width: '25%', padding: '6px 12px', textAlign: 'center' }}>{data.mutationCounts?.Frameshift ? Number(data.mutationCounts.Frameshift).toLocaleString() : 0}</div>
                            </div>
                            <div style={{ display: 'flex', borderBottom: '1px solid #d1d5db', backgroundColor: 'rgb(250, 250, 250)' }}>
                                <div style={{ width: '75%', padding: '6px 12px', borderRight: '1px solid #d1d5db', textAlign: 'left' }}>Inframe Insertion</div>
                                <div style={{ width: '25%', padding: '6px 12px', textAlign: 'center' }}>{data.mutationCounts?.InframeIns ? Number(data.mutationCounts.InframeIns).toLocaleString() : 0}</div>
                            </div>
                            <div style={{ display: 'flex', backgroundColor: 'rgb(250, 250, 250)' }}>
                                <div style={{ width: '75%', padding: '6px 12px', borderRight: '1px solid #d1d5db', textAlign: 'left' }}>Inframe Deletion</div>
                                <div style={{ width: '25%', padding: '6px 12px', textAlign: 'center' }}>{data.mutationCounts?.InframeDel ? Number(data.mutationCounts.InframeDel).toLocaleString() : 0}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <ul className={styles.description} style={{ ...s_desc, paddingLeft: '20px', listStyleType: 'disc' }}>
                    <li style={{ marginBottom: '4px' }}><strong>Number of SNP :</strong> Total Number of SNP</li>
                    <li style={{ marginBottom: '4px' }}><strong>Synonymous Variant :</strong> Variant causes a codon that produces the same amino acid e.g.: Ttg/Ctg, L/L</li>
                    <li style={{ marginBottom: '4px' }}><strong>Missense Variant :</strong> Variant causes a codon that produces a different amino acid e.g.: Tgg/Cgg, W/R</li>
                    <li style={{ marginBottom: '4px' }}><strong>Stop Gained :</strong> Variant causes a STOP codon e.g.: Cag/Tag, Q/*</li>
                    <li style={{ marginBottom: '4px' }}><strong>Stop Lost :</strong> Variant causes stop codon to be mutated into a non-stop codon e.g.: Tga/Cga, */R</li>
                    <li style={{ marginBottom: '4px' }}><strong>Number of INDEL :</strong> Total Number of INDEL</li>
                    <li style={{ marginBottom: '4px' }}><strong>Frameshift Variant :</strong> Insertion or deletion causes a frame shift e.g.: An indel size is not multple of 3</li>
                    <li style={{ marginBottom: '4px' }}><strong>Inframe Insertion :</strong> One or many codons are inserted e.g.: An insert multiple of three in a codon boundary</li>
                    <li><strong>Inframe Deletion :</strong> An inframe non synonymous variant that deletes bases from the coding sequence</li>
                </ul>

                {renderFooter(pageNum_Som1)}
            </div>

            {/* --- Page 16: Somatic Variant Call (Column Description + Table) --- */}
            <div className={`pdf-page ${styles.a4Page}`}>
                {renderPageHeader(`${secSomatic}. Somatic Variant`)}

                <h3 className={styles.sectionTitle} style={{ ...s_secTitle, marginTop: '0' }}>
                    Somatic Variant Result Table
                    <button
                        onClick={() => handleDownloadMutation('somatic')}
                        style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', marginLeft: '10px' }}
                    >
                        (<span style={{ textDecoration: 'underline' }}>Download</span>)
                    </button>
                </h3>

                <table className={styles.dataTable} style={{ ...descTableStyle, fontSize: '9px', marginBottom: '0' }}>
                    <thead>
                        <tr>
                            <th style={{ width: '20%', ...descThStyle }}>Column</th>
                            <th style={descThStyle}>Description</th>
                        </tr>
                    </thead>
                    <tbody style={{ lineHeight: '1.3' }}>
                        {variantColumns.map((item, idx) => (
                            <tr key={idx}>
                                <td style={descColTdStyle}><strong>{item.col}</strong></td>
                                <td style={descTextTdStyle}>{item.desc}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {renderFooter(pageNum_Som2)}
            </div>

            {/* 조건부 렌더링: G01, G04, G06 인 경우에만 Germline 페이지 출력 */}
            {isGermlineTarget && (
                <>
                    {/* --- Page 17: Germline Variant Call (Mutation Summary) --- */}
                    <div className={`pdf-page ${styles.a4Page}`} id="sec-germline">
                        {renderPageHeader(`${secGermline}. Germline Variant Call`)}

                        <h3 className={styles.sectionTitle} style={{ ...s_secTitle, marginTop: '0', marginBottom: '8px' }}>Mutation Summary</h3>

                        <div style={{ display: 'flex', width: '85%', margin: '0 auto 20px auto', border: '1px solid #d1d5db', fontSize: '10.5px', color: '#4b5563' }}>
                            <div style={{ width: '30%', display: 'flex', flexDirection: 'column', borderRight: '1px solid #d1d5db' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgb(243, 244, 246)', fontWeight: 'bold', color: '#374151', borderBottom: '1px solid #d1d5db', padding: '6px 12px', height: '27px', boxSizing: 'border-box' }}>
                                    Sample Name
                                </div>
                                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9fafb', fontWeight: 'bold', color: '#374151', padding: '8px', wordBreak: 'keep-all' }}>
                                    {data.pairedSample || data.sampleName}
                                </div>
                            </div>

                            <div style={{ width: '70%', display: 'flex', flexDirection: 'column' }}>
                                <div style={{ display: 'flex', backgroundColor: 'rgb(243, 244, 246)', fontWeight: 'bold', color: '#374151', borderBottom: '1px solid #d1d5db', textAlign: 'center', height: '27px', boxSizing: 'border-box' }}>
                                    <div style={{ width: '70%', padding: '6px 12px', borderRight: '1px solid #d1d5db' }}>Category</div>
                                    <div style={{ width: '30%', padding: '6px 12px' }}>Count</div>
                                </div>

                                <div style={{ display: 'flex', borderBottom: '1px solid #d1d5db' }}>
                                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                                        <div style={{ display: 'flex', borderBottom: '1px solid #d1d5db', backgroundColor: 'rgb(250, 250, 250)' }}>
                                            <div style={{ width: '70%', padding: '6px 12px', borderRight: '1px solid #d1d5db', textAlign: 'left' }}>SNP</div>
                                            <div style={{ width: '30%', padding: '6px 12px', textAlign: 'center' }}>{data.germlineCounts?.SNP ? Number(data.germlineCounts.SNP).toLocaleString() : 0}</div>
                                        </div>
                                        <div style={{ display: 'flex', backgroundColor: 'rgb(250, 250, 250)' }}>
                                            <div style={{ width: '70%', padding: '6px 12px', borderRight: '1px solid #d1d5db', textAlign: 'left' }}>INDEL (INS/DEL)</div>
                                            <div style={{ width: '30%', padding: '6px 12px', textAlign: 'center' }}>{data.germlineCounts?.INDEL ? Number(data.germlineCounts.INDEL).toLocaleString() : 0}</div>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ display: 'flex' }}>
                                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                                        <div style={{ display: 'flex', borderBottom: '1px solid #d1d5db', backgroundColor: 'rgb(250, 250, 250)' }}>
                                            <div style={{ width: '70%', padding: '6px 12px', borderRight: '1px solid #d1d5db', textAlign: 'left' }}>Synonymous Variant</div>
                                            <div style={{ width: '30%', padding: '6px 12px', textAlign: 'center' }}>{data.germlineCounts?.Synonymous ? Number(data.germlineCounts.Synonymous).toLocaleString() : 0}</div>
                                        </div>
                                        <div style={{ display: 'flex', borderBottom: '1px solid #d1d5db', backgroundColor: 'rgb(250, 250, 250)' }}>
                                            <div style={{ width: '70%', padding: '6px 12px', borderRight: '1px solid #d1d5db', textAlign: 'left' }}>Missense Variant</div>
                                            <div style={{ width: '30%', padding: '6px 12px', textAlign: 'center' }}>{data.germlineCounts?.Missense ? Number(data.germlineCounts.Missense).toLocaleString() : 0}</div>
                                        </div>
                                        <div style={{ display: 'flex', borderBottom: '1px solid #d1d5db', backgroundColor: 'rgb(250, 250, 250)' }}>
                                            <div style={{ width: '70%', padding: '6px 12px', borderRight: '1px solid #d1d5db', textAlign: 'left' }}>Stop Gain</div>
                                            <div style={{ width: '30%', padding: '6px 12px', textAlign: 'center' }}>{data.germlineCounts?.StopGain ? Number(data.germlineCounts.StopGain).toLocaleString() : 0}</div>
                                        </div>
                                        <div style={{ display: 'flex', borderBottom: '1px solid #d1d5db', backgroundColor: 'rgb(250, 250, 250)' }}>
                                            <div style={{ width: '70%', padding: '6px 12px', borderRight: '1px solid #d1d5db', textAlign: 'left' }}>Stop Lost</div>
                                            <div style={{ width: '30%', padding: '6px 12px', textAlign: 'center' }}>{data.germlineCounts?.StopLost ? Number(data.germlineCounts.StopLost).toLocaleString() : 0}</div>
                                        </div>
                                        <div style={{ display: 'flex', borderBottom: '1px solid #d1d5db', backgroundColor: 'rgb(250, 250, 250)' }}>
                                            <div style={{ width: '70%', padding: '6px 12px', borderRight: '1px solid #d1d5db', textAlign: 'left' }}>Frameshift Variant</div>
                                            <div style={{ width: '30%', padding: '6px 12px', textAlign: 'center' }}>{data.germlineCounts?.Frameshift ? Number(data.germlineCounts.Frameshift).toLocaleString() : 0}</div>
                                        </div>
                                        <div style={{ display: 'flex', borderBottom: '1px solid #d1d5db', backgroundColor: 'rgb(250, 250, 250)' }}>
                                            <div style={{ width: '70%', padding: '6px 12px', borderRight: '1px solid #d1d5db', textAlign: 'left' }}>Inframe Insertion</div>
                                            <div style={{ width: '30%', padding: '6px 12px', textAlign: 'center' }}>{data.germlineCounts?.InframeIns ? Number(data.germlineCounts.InframeIns).toLocaleString() : 0}</div>
                                        </div>
                                        <div style={{ display: 'flex', backgroundColor: 'rgb(250, 250, 250)' }}>
                                            <div style={{ width: '70%', padding: '6px 12px', borderRight: '1px solid #d1d5db', textAlign: 'left' }}>Inframe Deletion</div>
                                            <div style={{ width: '30%', padding: '6px 12px', textAlign: 'center' }}>{data.germlineCounts?.InframeDel ? Number(data.germlineCounts.InframeDel).toLocaleString() : 0}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <ul className={styles.description} style={{ ...s_desc, paddingLeft: '20px', listStyleType: 'disc' }}>
                            <li style={{ marginBottom: '4px' }}><strong>Number of SNP :</strong> Total Number of SNP</li>
                            <li style={{ marginBottom: '4px' }}><strong>Synonymous Variant :</strong> Variant causes a codon that produces the same amino acid e.g.: Ttg/Ctg, L/L</li>
                            <li style={{ marginBottom: '4px' }}><strong>Missense Variant :</strong> Variant causes a codon that produces a different amino acid e.g.: Tgg/Cgg, W/R</li>
                            <li style={{ marginBottom: '4px' }}><strong>Stop Gained :</strong> Variant causes a STOP codon e.g.: Cag/Tag, Q/*</li>
                            <li style={{ marginBottom: '4px' }}><strong>Stop Lost :</strong> Variant causes stop codon to be mutated into a non-stop codon e.g.: Tga/Cga, */R</li>
                            <li style={{ marginBottom: '4px' }}><strong>Number of INDEL :</strong> Total Number of INDEL</li>
                            <li style={{ marginBottom: '4px' }}><strong>Frameshift Variant :</strong> Insertion or deletion causes a frame shift e.g.: An indel size is not multple of 3</li>
                            <li style={{ marginBottom: '4px' }}><strong>Inframe Insertion :</strong> One or many codons are inserted e.g.: An insert multiple of three in a codon boundary</li>
                            <li><strong>Inframe Deletion :</strong> An inframe non synonymous variant that deletes bases from the coding sequence</li>
                        </ul>

                        {renderFooter(pageNum_Germ1)}
                    </div>

                    {/* --- Page 18: Germline Variant Call (Column Description) --- */}
                    <div className={`pdf-page ${styles.a4Page}`}>
                        {renderPageHeader(`${secGermline}. Germline Variant Call`)}

                        <h3 className={styles.sectionTitle} style={s_secTitle}>
                            Germline Variant Result Table
                            <button
                                onClick={() => handleDownloadMutation('germline')}
                                style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', textDecoration: 'underline', marginLeft: '10px' }}
                            >
                                (Download)
                            </button>
                        </h3>

                        <table className={styles.dataTable} style={{ ...descTableStyle, fontSize: '9px', marginBottom: '0' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: '20%', ...descThStyle }}>Column</th>
                                    <th style={descThStyle}>Description</th>
                                </tr>
                            </thead>
                            <tbody style={{ lineHeight: '1.3' }}>
                                {variantColumns.map((item, idx) => (
                                    <tr key={idx}>
                                        <td style={descColTdStyle}><strong>{item.col}</strong></td>
                                        <td style={descTextTdStyle}>{item.desc}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {renderFooter(pageNum_Germ2)}
                    </div>
                </>
            )}

            {/* --- Page 19: Tumor Purity (Spider Plot) --- */}
            <div className={`pdf-page ${styles.a4Page}`} id="sec-purity">
                {renderPageHeader(`${secPurity}. Tumor Purity`)}

                <h3 style={s_paraTitle}>Tumor Purity Analysis</h3>
                <p className={styles.description} style={s_desc}>
                    Since tumor tissues are a mixture of tumor cells and normal cells, it is important to determine the proportion of tumor cells (tumor purity) in the actual data. FACETS jointly estimates tumor purity and ploidy using the sequenced heterozygous allele ratio (logOR) and coverage changes (logR).
                </p>

                <h3 style={s_paraTitle}>Tumor purity–ploidy joint likelihood surface plot</h3>
                <p className={styles.description} style={s_desc}>
                    All curves represent the expected logR/logOR patterns at various ploidy-purity combinations. The purple dot indicates the combination that best fits the actual data (maximum likelihood). The further right (+) the purple dot is, the higher the ploidy of the tumor, and the higher it is, the higher the proportion of tumor cells, indicating higher purity.
                </p>

                <div className={styles.imageContainer} style={{ maxHeight: '350px', marginBottom: '20px' }}>
                    {data.spiderImg ? (
                        <img src={data.spiderImg} alt="Tumor Purity Spider Plot" />
                    ) : (
                        <span className={styles.errorText}>[Spider Plot image not available]</span>
                    )}
                </div>

                <h3 className={styles.sectionTitle} style={s_secTitle}>Graph Description</h3>
                <table className={styles.dataTable} style={descTableStyle}>
                    <thead>
                        <tr>
                            <th style={{ width: '20%', ...descThStyle }}></th>
                            <th style={descThStyle}>Description</th>
                        </tr>
                    </thead>
                    <tbody style={{ lineHeight: '1.4' }}>
                        <tr>
                            <td style={descColTdStyle}><strong>X-axis</strong></td>
                            <td style={descTextTdStyle}>
                                Expected(logR − dipLogR): Difference in expected logR (coverage ratio) values. In other words, the change in logR according to ploidy.
                            </td>
                        </tr>
                        <tr>
                            <td style={descColTdStyle}><strong>Y-axis</strong></td>
                            <td style={descTextTdStyle}>
                                Expected(logOR): Expected log odds ratio (allele ratio).
                            </td>
                        </tr>
                        <tr>
                            <td style={descColTdStyle}><strong>Purple dot</strong></td>
                            <td style={descTextTdStyle}>
                                The combination that best fits the actual data (maximum likelihood). The further right (+) the purple dot is, the higher the ploidy of the tumor, and the higher it is, the higher the proportion of tumor cells, indicating higher purity.
                            </td>
                        </tr>
                        <tr>
                            <td style={descColTdStyle}><strong>Curves</strong></td>
                            <td style={descTextTdStyle}>
                                Expected logR and logOR values for each (ploidy)-(purity) combination.
                            </td>
                        </tr>
                    </tbody>
                </table>

                {renderFooter(pageNum_Pur1)}
            </div>

            {/* --- Page 20: Tumor Purity (CNV Plot) --- */}
            <div className={`pdf-page ${styles.a4Page}`}>
                {renderPageHeader(`${secPurity}. Tumor Purity`)}

                <h3 className={styles.sectionTitle} style={{ ...s_secTitle, fontWeight: 'bold', color: '#000' }}>Tumor purity CNV plot</h3>

                <div className={styles.description}>
                    <p style={{ ...s_desc, fontWeight: 'bold', marginBottom: '8px' }}>
                        This graph shows the estimated tumor purity, ploidy, and copy number variation (CNV) results by chromosome.
                    </p>
                    <ul style={{ ...s_desc, paddingLeft: '20px', listStyleType: 'disc', marginBottom: '20px' }}>
                        <li style={{ marginBottom: '4px' }}><strong>First panel:</strong> Shows the overall copy number change pattern through the log-ratio (change in read depth of tumor vs. normal).</li>
                        <li style={{ marginBottom: '4px' }}><strong>Second panel:</strong> The log-odds-ratio indicates the presence of allelic imbalance (LOH) based on the B-allele frequency.</li>
                        <li><strong>Third panel:</strong> The red line represents the estimated major/minor copy number status, and the black line represents the segment copy number at the exon level.</li>
                    </ul>
                </div>

                <div className={styles.imageContainer} style={{ maxHeight: '550px', marginBottom: '20px' }}>
                    {data.purityCnvImg ? (
                        <img src={data.purityCnvImg} alt="Tumor Purity CNV Plot" />
                    ) : (
                        <span className={styles.errorText}>[CNV Plot image not available]</span>
                    )}
                </div>

                {renderFooter(pageNum_Pur2)}
            </div>

            {/* --- Page 21: Variant Correlation --- */}
            {hasCorrelation && (
                <div className={`pdf-page ${styles.a4Page}`} id="sec-correlation">
                    {renderPageHeader(`${secCorrelation}. Variant Correlation`)}

                    <h3 style={s_paraTitle}>Somatic variant overlap (Venn Diagram)</h3>
                    <p className={styles.description} style={s_desc}>
                        The venn diagram shows the overlap of somatic variants detected in the tumor organoid (TO) and tumor tissue (TT). The numbers indicate the counts of somatic variants called in each sample and those shared between them. Sample names are shown below each circle
                    </p>

                    <h3 className={styles.sectionTitle} style={{ ...s_secTitle, marginTop: '20px' }}>Metrics Summary</h3>

                    {(() => {
                        const sharedCount = Number(data.correlationSummary.sharedCount) || 0;
                        const toTotal = (Number(data.correlationSummary.tumorUniqueCount) || 0);
                        const ttTotal = (Number(data.correlationSummary.pairUniqueCount) || 0);

                        const toConcordance = toTotal > 0 ? ((sharedCount / toTotal) * 100).toFixed(2) : '0.00';
                        const ttConcordance = ttTotal > 0 ? ((sharedCount / ttTotal) * 100).toFixed(2) : '0.00';

                        return (
                            <table className={styles.dataTable} style={{ ...s_table, tableLayout: 'fixed', width: '100%', marginBottom: '30px' }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: '70%', textAlign: 'left' }}>Metric</th>
                                        <th style={{ width: '30%', textAlign: 'center' }}>Value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td style={{ textAlign: 'left' }}>Tumor organoid (TO) variants</td>
                                        <td style={{ textAlign: 'center' }}>{toTotal.toLocaleString()}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ textAlign: 'left' }}>Tumor tissue (TT) variants</td>
                                        <td style={{ textAlign: 'center' }}>{ttTotal.toLocaleString()}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ textAlign: 'left' }}>Shared variants</td>
                                        <td style={{ textAlign: 'center' }}>{sharedCount.toLocaleString()}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ textAlign: 'left' }}>TO-based concordance (%)</td>
                                        <td style={{ textAlign: 'center' }}>{toConcordance}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ textAlign: 'left' }}>TT-based concordance (%)</td>
                                        <td style={{ textAlign: 'center' }}>{ttConcordance}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ textAlign: 'left' }}>Overall concordance (%)</td>
                                        <td style={{ textAlign: 'center' }}>
                                            {data.correlationSummary.variantOverlapPercent ? `${data.correlationSummary.variantOverlapPercent}` : '-'}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        );
                    })()}

                    {renderVennDiagram(data.correlationSummary)}

                    {renderFooter(pageNum_Corr)}
                </div>
            )}
        </div>
    );
}

export default WESInnerReportComponent;